import { Input } from '../../common/input.component';
import { Link } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { AuthService } from '../../../services/auth.service';
import { ProfileService } from '../../../services/profile.service';
import { phoneValidation, required } from '../../../utils/validations';
import React, { useCallback, useState } from 'react';
import { InvisibleSmartCaptcha } from '@yandex/smart-captcha';

type Fields = {
  phone: string;
  password: string;
};

type Props = {
  userIsClient: boolean;
};

export const LoginScreen = ({ userIsClient }: Props) => {
  const {
    register,
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<Fields>();

  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaVisible, setCaptchaVisible] = useState(false);

  const handleChallengeHidden = useCallback(() => setCaptchaVisible(false), []);
  const triggerCaptcha = () => setCaptchaVisible(true);

  const onSubmit =
    (token: string) =>
    ({ phone, password }: Fields) => {
      AuthService.login({
        username: phone,
        password,
        source: 'admin',
        captchaToken: token,
      }).then(() => ProfileService.getCurrentUser());
    };

  return (
    <div className="full-height d-flex flex-column justify-content-between">
      <div className="flex-grow-1 d-flex justify-content-center align-items-center">
        <div className="auth-wrapper">
          <h3 className="mb-4">Войти в личный кабинет</h3>
          <form
            className="needs-validation"
            onSubmit={handleSubmit(onSubmit(captchaToken))}
          >
            <InvisibleSmartCaptcha
              sitekey={import.meta.env.VITE_YANDEX_CAPTCHA_KEY || ''}
              hideShield
              test={Boolean(import.meta.env.VITE_YANDEX_CAPTCHA_TEST || false)}
              visible={captchaVisible}
              onChallengeHidden={handleChallengeHidden}
              onTokenExpired={() => setCaptchaToken('')}
              // Third-party SmartCaptcha script failures (WEB-88): network
              // blocked, adblock/CSP rejecting smartcaptcha.yandexcloud.net,
              // headless Chrome scraping, etc. SmartCaptcha retries itself;
              // pushing a Sentry event from here is non-actionable noise.
              // Reset the token so the next submit re-triggers the challenge.
              onNetworkError={() => setCaptchaToken('')}
              onJavascriptError={() => setCaptchaToken('')}
              onSuccess={(token) => {
                setCaptchaToken(token);
                handleChallengeHidden();
                handleSubmit(onSubmit(token))();
              }}
            />
            {userIsClient ? (
              <div className="mb-4 color-red">
                Этот аккаунт другого домена
                <br />
                这个账号是另外一个域名的
              </div>
            ) : null}
            <Controller
              name="phone"
              control={control}
              rules={{ ...required, ...phoneValidation }}
              render={({ field }) => (
                <Input
                  title="Номер телефона"
                  className="mb-4"
                  placeholder="+7"
                  mask="+7 (999) 999-99-99"
                  {...field}
                  error={errors.phone?.message}
                />
              )}
            />
            <Input
              type="password"
              {...register('password', {
                ...required,
              })}
              error={errors.password?.message}
              title="Пароль"
            />
            <Link
              to="/forgot-password"
              className="btn-link d-block link-success mb-4"
            >
              Забыли пароль?
            </Link>
            <button
              type="submit"
              className="btn btn-success w-100"
              disabled={captchaVisible}
              onClick={(e) => {
                if (!captchaToken) {
                  e.preventDefault();
                  triggerCaptcha();
                }
              }}
            >
              Войти
            </button>
          </form>
        </div>
      </div>
      <div />
    </div>
  );
};
